'use client';
import ModalWrapper from '@/components/Modals/ModalWrapper/ModalWrapper';
import React, { use, useEffect, useMemo, useState } from 'react';
import ds from '@/styles/ds/ds.module.scss';
import Image from 'next/image';
import {
	useModalStoreActions,
	useVipActions,
	useVipLevelUpData,
} from '@/store';
import { VipLevelUp } from '@/store/vipStore/types';
import Link from 'next/link';
import { useGetVip } from '@/backend/vip/get-vip';

const LevelUpModal = () => {
	const vipLevelUpData = useVipLevelUpData();
	const { setVipLevelUpData } = useVipActions();
	const { setModalStatus } = useModalStoreActions();

	const handleClose = () => {
		if (vipLevelUpData?.length) {
			setVipLevelUpData(vipLevelUpData.slice(1));
		}
	};

	useEffect(() => {
		if (!vipLevelUpData?.length) {
			setModalStatus(null);
		}
	}, [vipLevelUpData]);

	if (!vipLevelUpData?.length) return null;

	return (
		<ModalWrapper onClose={handleClose} disableClose>
			<VipLevelUpContent
				vipLevelUpData={vipLevelUpData[0]}
				onClose={handleClose}
			/>
		</ModalWrapper>
	);
};

export default LevelUpModal;

interface VipLevelUpContentProps {
	vipLevelUpData: VipLevelUp;
	onClose: () => void;
}

const VipLevelUpContent = ({
	vipLevelUpData,
	onClose,
}: VipLevelUpContentProps) => {
	const { getVip } = useGetVip();
	const handleClose = () => {
		onClose();
	};

	/* -------------------------------------------------------------------------- */
	const levelUpBackground = useMemo(() => {
		const tierName = vipLevelUpData?.tierName?.toLowerCase();

		switch (tierName) {
			case 'owner':
				return {
					backgroundStyles: {
						background: 'linear-gradient(0deg, #C89CFF 0%, #785E99 100%)',
					},
					overlayStyles: {
						background:
							'linear-gradient(to top, #C89CFF 30%, rgba(0,0,0,0) 75%)',
					},
				};
			case 'entrepreneur':
				return {
					backgroundStyles: {
						background: 'linear-gradient(0deg, #6764FC 0%, #3B4A8B 100%)',
					},
					overlayStyles: {
						background:
							'linear-gradient(to top, #6764FC 30%, rgba(0,0,0,0) 75%)',
					},
				};
			case 'jetsetter':
				return {
					backgroundStyles: {
						background: 'linear-gradient(0deg,  #405FFF 0%, #1B286D 100%)',
					},
					overlayStyles: {
						background:
							'linear-gradient(to top, #405FFF 30%, rgba(0,0,0,0) 75%)',
					},
				};
			case 'landlord':
				return {
					backgroundStyles: {
						background: 'linear-gradient(0deg,  #219CFF 0%, #09365B 100%)',
					},
					overlayStyles: {
						background:
							'linear-gradient(to top, #219CFF 30%, rgba(0,0,0,0) 75%)',
					},
				};
			case 'investor':
				return {
					backgroundStyles: {
						background: 'linear-gradient(0deg, #13C6FF 0%, #0D5A63 100%)',
					},
					overlayStyles: {
						background:
							'linear-gradient(to top, #13C6FF 30%, rgba(0,0,0,0) 75%)',
					},
				};
			case 'mogul':
				return {
					backgroundStyles: {
						background: 'linear-gradient(0deg,  #0FBD9A 0%, #045135 100%)',
					},
					overlayStyles: {
						background:
							'linear-gradient(to top, #0FBD9A 30%, rgba(0,0,0,0) 75%)',
					},
				};
			case 'magnate':
				return {
					backgroundStyles: {
						background:
							'linear-gradient(0deg, rgba(23, 26, 30, 0.00) 31.85%, rgba(23, 26, 30, 0.70) 100%), linear-gradient(0deg, #FD4E5C 0%, #D6BB64 33.33%, #9AAD9A 49%, #579ED7 66.67%, #D06FF7 100%)',
					},
					overlayStyles: {
						background:
							'linear-gradient(to top, rgba(23, 26, 30, 0.00) 31.85%, rgba(23, 26, 30, 0.00) 100%)',
					},
				};
			case 'benefactor':
				return {
					backgroundStyles: {
						backgroundImage: "url('/img/level_up_benefactor.webp')",
						backgroundSize: 'cover',
						backgroundPosition: 'center',
						backgroundRepeat: 'no-repeat',
					},
					overlayStyles: {
						background:
							'linear-gradient(to top, rgba(23, 26, 30, 0.00) 31.85%, rgba(23, 26, 30, 0.00) 100%)',
					},
				};
			default:
				return {
					backgroundStyles: {
						background: 'linear-gradient(0deg, #C89CFF 0%, #785E99 100%)',
					},
					overlayStyles: {
						background:
							'linear-gradient(to top, #C89CFF 30%, rgba(0,0,0,0) 75%)',
					},
				};
		}
	}, [vipLevelUpData]);
	/* -------------------------------------------------------------------------- */
	const cashBonus = useMemo(() => {
		return (
			vipLevelUpData?.bonuses?.find((b) => b.type === 'CASH_BONUS') ?? null
		);
	}, [vipLevelUpData]);

	const freespinBonus = useMemo(() => {
		return (
			vipLevelUpData?.bonuses?.find((b) => b.type === 'FREESPIN_BONUS') ?? null
		);
	}, [vipLevelUpData]);
	const depositBonus = useMemo(() => {
		return (
			vipLevelUpData?.bonuses?.find((b) => b.type === 'DEPOSIT_BONUS') ?? null
		);
	}, [vipLevelUpData]);

	return (
		<div
			className="   w-screen max-w-[350px] h-fit top-0 m-auto   p-5  md:m-auto flex rounded-[10px] flex-col gap-5 popup overflow-hidden relative  "
			// style={{
			// 	// background: levelUpBackground.background,
			// 	backgroundImage: "url('/img/level_up_benefactor.webp')",
			// 	backgroundSize: 'cover',
			// 	backgroundPosition: 'center',
			// 	backgroundRepeat: 'no-repeat',
			// }}
			style={levelUpBackground.backgroundStyles}
		>
			<button onClick={handleClose}>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="30"
					height="30"
					viewBox="0 0 30 30"
					fill="none"
					className="absolute top-5 right-5 cursor-pointer z-[2] hover:scale-110 transition-transform duration-200"
				>
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M22.5759 9.4715C23.1414 8.90611 23.1414 7.98943 22.5759 7.42404C22.0105 6.85865 21.0938 6.85865 20.5284 7.42404L14.9996 12.9527L9.47157 7.42479C8.90616 6.8594 7.98946 6.8594 7.42405 7.42479C6.85865 7.99018 6.85865 8.90686 7.42405 9.47224L12.9521 15.0001L7.42434 20.5278C6.85893 21.0931 6.85893 22.0098 7.42434 22.5752C7.98974 23.1406 8.90645 23.1406 9.47185 22.5752L14.9996 17.0476L20.5281 22.576C21.0936 23.1413 22.0103 23.1413 22.5757 22.576C23.1411 22.0106 23.1411 21.0939 22.5757 20.5285L17.0471 15.0001L22.5759 9.4715Z"
						fill="#F2F3F5"
					/>
				</svg>
			</button>
			<div
				className="absolute top-0 left-0 h-full w-full z-[1] "
				style={levelUpBackground.overlayStyles}
			></div>
			<div className="flex flex-col gap-5">
				<div className="flex  flex-col items-center">
					<div className=" aspect-square w-[50%]  max-w-[350px] mx-auto relative z-[-1]  translate-y-[8%]">
						<Image
							src={vipLevelUpData?.imageUrl || ''}
							alt="Level Up"
							fill
							className="object-contain  scale-[2]"
						/>
					</div>
					<p
						className={`${ds.section_header_secondary} text-dove-white-o-70 z-[2]`}
					>
						New Property Unlocked!
					</p>
					<p className=" text-[38px]  leading-[1] uppercase font-anton z-[2]">
						{vipLevelUpData?.propertyName}
					</p>
				</div>
				<div className="flex flex-col gap-2 z-[2]">
					<div className="grid grid-cols-2 gap-2">
						<div
							className="flex flex-col gap-1.5 items-center px-2 py-1"
							style={{
								borderRadius: '10px',
								border: '1px solid rgba(242, 243, 245, 0.25)',
								background: 'rgba(242, 243, 245, 0.09)',
							}}
						>
							<p className={`${ds.page_title_primary} !mb-0`}>
								{freespinBonus?.freespinBonus?.quantity || 0}
							</p>
							<p className=" text-xs">Spins</p>
						</div>
						{/* -------------------------------------------------------------------------- */}
						{/* <div
							className="flex flex-col gap-1.5 items-center px-2 py-1"
							style={{
								borderRadius: '10px',
								border: '1px solid rgba(242, 243, 245, 0.25)',
								background: 'rgba(242, 243, 245, 0.09)',
							}}
						>
							<p className={`${ds.page_title_primary} !mb-0`}>0%</p>
							<p className=" text-xs">Instant Rakeback</p>
						</div> */}
						{/* -------------------------------------------------------------------------- */}
						<div
							className="flex flex-col gap-1.5 items-center px-2 py-1"
							style={{
								borderRadius: '10px',
								border: '1px solid rgba(242, 243, 245, 0.25)',
								background: 'rgba(242, 243, 245, 0.09)',
							}}
						>
							<p className={`${ds.page_title_primary} !mb-0`}>
								€
								{parseFloat(cashBonus?.cashBonus?.fiatAmount || '0').toFixed(2)}
							</p>
							<p className=" text-xs">Bonus</p>
						</div>
						{/* -------------------------------------------------------------------------- */}
						{/* -------------------------------------------------------------------------- */}
						{/* <div
							className="flex flex-col gap-1.5 items-center px-2 py-1"
							style={{
								borderRadius: '10px',
								border: '1px solid rgba(242, 243, 245, 0.25)',
								background: 'rgba(242, 243, 245, 0.09)',
							}}
						>
							<p className={`${ds.page_title_primary} !mb-0`}>
								€
								{parseFloat(cashBonus?.cashBonus?.fiatAmount || '0').toFixed(2)}
							</p>
							<p className=" text-xs">Weekly Spins</p>
						</div> */}
						{/* -------------------------------------------------------------------------- */}
						{/* -------------------------------------------------------------------------- */}
						{/* <div
							className="flex flex-col gap-1.5 items-center px-2 py-1"
							style={{
								borderRadius: '10px',
								border: '1px solid rgba(242, 243, 245, 0.25)',
								background: 'rgba(242, 243, 245, 0.09)',
							}}
						>
							<p className={`${ds.page_title_primary} !mb-0`}>
								€
								{parseFloat(cashBonus?.cashBonus?.fiatAmount || '0').toFixed(2)}
							</p>
							<p className=" text-xs">Weekly Bonus</p>
						</div> */}
						{/* -------------------------------------------------------------------------- */}
					</div>
					{vipLevelUpData?.level >= 6 ? (
						<div
							className="flex flex-col gap-1.5 items-center px-2 py-1"
							style={{
								borderRadius: '10px',
								border: '1px solid rgba(242, 243, 245, 0.25)',
								background: 'rgba(242, 243, 245, 0.09)',
							}}
						>
							<p className={`${ds.page_title_primary} !mb-0`}>
								{parseInt(depositBonus?.depositBonus?.depositPercentage || '0')}
								%
							</p>
							<p className=" text-xs">Deposit Bonus</p>
						</div>
					) : null}
				</div>
			</div>
			<Link
				href={'/vip/rewards'}
				className={`${ds.primary} ${ds.greenBlack} !h-10 z-[2]`}
				onClick={() => {
					getVip();
					handleClose();
				}}
			>
				Claim Rewards
			</Link>
		</div>
	);
};
